import { useCallback } from "react";
import { useQueryClient } from "react-query";
import errorNotification from "../Components/Notification/errorNotification";
import successNotification from "../Components/Notification/successNotification";
import { useFetchWithRetry } from "../Utils/fetchWithRetry";
import { PostWithRetry } from "../Utils/postWithRetry";
import { cachedRequest } from "../Utils/cachedRequest";
import { toast } from "react-toastify";
import { logError, logInfo } from "../Utils/loggers";
import { postmortemApiEndpoint } from "../config";

export const ImageServices = () => {
  const { postWithRetry } = PostWithRetry();
  const { fetchWithRetry } = useFetchWithRetry();
  const queryClient = useQueryClient();
  const apiEndpoint = postmortemApiEndpoint; // Use direct ingress for image operations

  const postImage = async (ImageData, tags, incidentID) => {
    logInfo("postImage", "uploading images");
    try {
      const url = apiEndpoint + `/images-upload?incidentNumber=${encodeURIComponent(incidentID)}`;
      const response = await postWithRetry(url, ImageData, tags);
      queryClient.invalidateQueries(["images", String(incidentID)]);
      toast.done("upload-progress");
      successNotification(tags[0].tag + " images saved successfully");
      logInfo("postImage", "uploaded images successfully");
      return response.data;
    } catch (error) {
      errorNotification(tags[0].tag + " images not saved");

      logError("postImage", "error uploading images", error)
      throw error;
    }
  };

  // One request per incident returns every tag; the raw response is cached and processed per call,
  // so the cached arrays are never mutated. If the all-tags endpoint is unavailable (for example an
  // older backend), fall back to the per-tag endpoint.
  const getImage = useCallback(
    async (imageStore, incidentID, tag) => {
      try {
        const id = String(incidentID);
        const allTags = await cachedRequest(queryClient, ["images", id, "__all__"], async () => {
          try {
            return await fetchWithRetry(
              `${apiEndpoint}/retrieve-images-all?incidentNumber=${encodeURIComponent(id)}`
            );
          } catch {
            return null;
          }
        });

        let image;
        if (allTags && typeof allTags === "object" && !Array.isArray(allTags)) {
          image = allTags[tag] || [];
        } else {
          const url =
            apiEndpoint +
            `/retrieve-images?incidentNumber=${incidentID}&tag=${tag}`;
          image = await cachedRequest(
            queryClient,
            ["images", id, String(tag)],
            () => fetchWithRetry(url)
          );
        }
        return processImageData(image, imageStore);
      } catch (error) {
        logError("getImage", "error retrieving images", error);

        throw error;
      }
    },
    [fetchWithRetry, apiEndpoint, queryClient]
  );

  // async (imageStore, incidentID, tag) => {

  //    try {
  //       const url = apiEndpoint + `/retrieve-images?incidentNumber=${incidentID}&tag=${tag}`
  //       const image = await fetchWithRetry(url)
  //       return processImageData(image, imageStore);
  //    }catch(error){

  //    }
  // }

  const processImageData = (data, imageStore) => {
    if (Array.isArray(data)) {
      data.forEach((record) => {
        if (typeof record === "object" && record !== null && "url" in record) {
          const { url, ...rest } = record;
          // Backend now returns a fully signed (server-side SAS) URL — use it directly
          imageStore.push({ ...rest, preview: url, uploaded: "true" });
        } else {
          console.error(
            "Record is not a valid object or does not contain a url property"
          );
        }
      });
    } else {
      console.error("Response data is not a valid array");
    }
    return imageStore;
  };
  return {
    postImage,
    getImage,
  };
};
